import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Badge,
    Breadcrumb,
    Button,
    Card,
    Col,
    Form,
    InputGroup,
    Row,
    Spinner,
    Table,
    Toast,
    ToastContainer,
} from 'react-bootstrap';
import axios from 'axios';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import { MultiSelect } from 'primereact/multiselect';
import config from '../../config';
import { useAuth } from '../../context/AuthContext';
import '../../assets/css/performance-review.css';

const STATUS_VARIANT = {
    draft: 'secondary',
    in_progress: 'info',
    completed: 'success',
    acknowledged: 'primary',
};

const STATUS_LABEL = {
    draft: 'Draft',
    in_progress: 'In progress',
    completed: 'Completed',
    acknowledged: 'Acknowledged',
};

const classificationClass = (label) => {
    if (!label) return '';
    if (label.includes('Leader')) return 'is-leader';
    if (label.includes('High')) return 'is-high';
    if (label.includes('Solid')) return 'is-solid';
    if (label.includes('Action')) return 'is-action';
    return 'is-critical';
};

const LEVEL_PREFIX = 'level:';

const PerformanceReviews = () => {
    const { accessToken } = useAuth();
    const navigate = useNavigate();
    const headers = useMemo(() => ({ Authorization: `Bearer ${accessToken}` }), [accessToken]);

    const [reviews, setReviews] = useState([]);
    const [templates, setTemplates] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(true);
    const [assigning, setAssigning] = useState(false);
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [monthFilter, setMonthFilter] = useState('');
    const [search, setSearch] = useState('');
    const [showAssign, setShowAssign] = useState(false);

    const [templateId, setTemplateId] = useState('');
    // Mixed selection: employee user_ids AND `level:<n>` pseudo-values
    const [selectionValues, setSelectionValues] = useState([]);
    const [orgLevels, setOrgLevels] = useState([]);
    const [periodStart, setPeriodStart] = useState('');
    const [periodEnd, setPeriodEnd] = useState('');

    const load = useCallback(async () => {
        if (!accessToken) return;
        setLoading(true);
        setError('');
        try {
            const [reviewsRes, templatesRes, employeesRes] = await Promise.all([
                axios.get(`${config.apiBASEURL}/performance-review`, { headers }),
                axios.get(`${config.apiBASEURL}/performance-review/templates?status=active`, { headers }),
                axios.get(`${config.apiBASEURL}/performance-review/employees`, { headers }),
            ]);
            setReviews(reviewsRes.data || []);
            setTemplates(templatesRes.data || []);
            const empPayload = employeesRes.data;
            // Support both legacy array and new { employees, levels } shape
            if (Array.isArray(empPayload)) {
                setEmployees(empPayload);
                setOrgLevels([]);
            } else {
                setEmployees(empPayload?.employees || []);
                setOrgLevels(empPayload?.levels || []);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Could not load performance reviews');
        } finally {
            setLoading(false);
        }
    }, [accessToken, headers]);

    useEffect(() => { load(); }, [load]);

    const filtered = useMemo(() => {
        let list = [...reviews];
        if (statusFilter !== 'all') list = list.filter((r) => r.status === statusFilter);
        if (monthFilter) {
            list = list.filter((r) => {
                const start = moment(r.review_period_start);
                const end = moment(r.review_period_end);
                const monthStart = moment(monthFilter, 'YYYY-MM').startOf('month');
                const monthEnd = moment(monthFilter, 'YYYY-MM').endOf('month');
                // Include reviews whose period overlaps the selected month
                return start.isSameOrBefore(monthEnd, 'day') && end.isSameOrAfter(monthStart, 'day');
            });
        }
        if (search.trim()) {
            const q = search.toLowerCase();
            list = list.filter(
                (r) =>
                    (r.employee?.name || '').toLowerCase().includes(q) ||
                    (r.employee?.email || '').toLowerCase().includes(q) ||
                    (r.template_name || '').toLowerCase().includes(q) ||
                    (r.role_title || '').toLowerCase().includes(q)
            );
        }
        return list;
    }, [reviews, statusFilter, monthFilter, search]);

    const stats = useMemo(() => ({
        total: filtered.length,
        draft: filtered.filter((r) => r.status === 'draft' || r.status === 'in_progress').length,
        completed: filtered.filter((r) => r.status === 'completed').length,
        acknowledged: filtered.filter((r) => r.status === 'acknowledged').length,
    }), [filtered]);

    const employeeOptions = useMemo(
        () =>
            employees.map((emp) => ({
                label: emp.level != null ? `${emp.name} (L${emp.level})` : emp.name,
                value: emp.user_id,
            })),
        [employees]
    );

    const levelOptions = useMemo(() => {
        const levels = orgLevels.length
            ? orgLevels
            : [...new Set(employees.map((e) => e.level).filter((l) => l != null))].sort((a, b) => a - b);
        return levels.map((l) => ({
            value: `${LEVEL_PREFIX}${l}`,
            label: `All L${l}`,
            count: employees.filter((e) => e.level === l).length,
        }));
    }, [orgLevels, employees]);

    // Grouped options for the single combined MultiSelect: "Org levels" + "Employees"
    const groupedSelectOptions = useMemo(() => {
        const groups = [];
        if (levelOptions.length) {
            groups.push({
                label: 'Org levels',
                code: 'levels',
                items: levelOptions.map((opt) => ({
                    label: `${opt.label} (${opt.count})`,
                    value: opt.value,
                })),
            });
        }
        groups.push({
            label: 'Employees',
            code: 'employees',
            items: employeeOptions,
        });
        return groups;
    }, [levelOptions, employeeOptions]);

    // Resolve mixed selectionValues (level tokens + user ids) into a flat list of employee ids
    const selectedEmployeeIds = useMemo(() => {
        const ids = new Set();
        selectionValues.forEach((v) => {
            if (typeof v === 'string' && v.startsWith(LEVEL_PREFIX)) {
                const levelNum = Number(v.slice(LEVEL_PREFIX.length));
                employees
                    .filter((e) => e.level === levelNum)
                    .forEach((e) => ids.add(e.user_id));
            } else {
                ids.add(v);
            }
        });
        return [...ids];
    }, [selectionValues, employees]);

    const selectedEmployees = useMemo(
        () => employees.filter((e) => selectedEmployeeIds.includes(e.user_id)),
        [employees, selectedEmployeeIds]
    );

    const removeSelectedEmployee = (userId) => {
        setSelectionValues((prev) => {
            // Directly selected as an individual — just drop it
            if (prev.includes(userId)) {
                return prev.filter((v) => v !== userId);
            }
            // Otherwise it must be part of a selected level — expand that level
            // into its individual members minus the one being removed
            const emp = employees.find((e) => e.user_id === userId);
            if (!emp || emp.level == null) return prev;
            const levelToken = `${LEVEL_PREFIX}${emp.level}`;
            if (!prev.includes(levelToken)) return prev;
            const remainingAtLevel = employees
                .filter((e) => e.level === emp.level && e.user_id !== userId)
                .map((e) => e.user_id);
            return [...prev.filter((v) => v !== levelToken), ...remainingAtLevel];
        });
    };

    const resetAssignForm = () => {
        setTemplateId('');
        setSelectionValues([]);
        setPeriodStart('');
        setPeriodEnd('');
    };

    const handleAssign = async () => {
        if (!templateId) {
            setError('Select a template.');
            return;
        }
        if (!selectedEmployeeIds.length) {
            setError('Select at least one employee or level.');
            return;
        }
        if (!periodStart || !periodEnd) {
            setError('Review period start and end are required.');
            return;
        }

        setAssigning(true);
        setError('');
        setMessage('');
        try {
            const res = await axios.post(
                `${config.apiBASEURL}/performance-review/templates/${templateId}/assign`,
                {
                    employee_ids: selectedEmployeeIds,
                    review_period_start: periodStart,
                    review_period_end: periodEnd,
                    status: 'in_progress',
                },
                { headers }
            );
            setMessage(`Assigned self-review to ${res.data.created_count} employee(s). They will get a notification and email.`);
            setShowAssign(false);
            resetAssignForm();
            await load();
        } catch (err) {
            setError(err.response?.data?.message || 'Could not assign template');
        } finally {
            setAssigning(false);
        }
    };

    return (
        <div className="pr-list-page">
            <ToastContainer position="top-end" className="p-3" style={{ position: 'fixed', zIndex: 1080 }}>
                <Toast bg="danger" show={!!error} onClose={() => setError('')} delay={5000} autohide>
                    <Toast.Header closeButton>
                        <strong className="me-auto">Error</strong>
                    </Toast.Header>
                    <Toast.Body className="text-white">{error}</Toast.Body>
                </Toast>
                <Toast bg="success" show={!!message} onClose={() => setMessage('')} delay={4000} autohide>
                    <Toast.Header closeButton>
                        <strong className="me-auto">Success</strong>
                    </Toast.Header>
                    <Toast.Body className="text-white">{message}</Toast.Body>
                </Toast>
            </ToastContainer>

            <Row className="body_content g-3">
                <Col xs={12}>
                    <Breadcrumb className="mb-0">
                        <Breadcrumb.Item
                            style={{ cursor: 'pointer' }}
                            onClick={() => navigate('/Graphe/dashboard/employee_list')}
                        >
                            Employee
                        </Breadcrumb.Item>
                        <Breadcrumb.Item active>Performance Reviews</Breadcrumb.Item>
                    </Breadcrumb>
                </Col>

                <Col xs={12}>
                    <div className="pr-list-header">
                        <div>
                            <h1 className="pr-page-title mb-1">Performance Reviews</h1>
                            <p className="pr-page-subtitle mb-0">
                                Assign a template to employees for self-review, then acknowledge submitted evaluations.
                            </p>
                        </div>
                        <Button
                            variant="primary"
                            className="pr-new-btn"
                            onClick={() => setShowAssign((v) => !v)}
                        >
                            {showAssign ? 'Close assign' : '+ Assign template'}
                        </Button>
                    </div>
                </Col>

                {showAssign && (
                    <Col xs={12}>
                        <Card className="mb-0 pr-assign-card">
                            <Card.Body>
                                <h5 className="mb-1">Assign template (self-review)</h5>
                                <p className="text-muted small mb-3">
                                    Pick a template, then choose individual employees or whole org levels
                                    (e.g. "All L2") from the same list. Remove anyone from the selection
                                    before assigning.
                                </p>
                                <Row className="g-3">
                                    <Col md={6}>
                                        <Form.Label>Template</Form.Label>
                                        <Form.Select
                                            value={templateId}
                                            onChange={(e) => setTemplateId(e.target.value)}
                                        >
                                            <option value="">Select template…</option>
                                            {templates.map((tpl) => (
                                                <option key={tpl.template_id} value={tpl.template_id}>
                                                    {tpl.name}
                                                </option>
                                            ))}
                                        </Form.Select>
                                        {!templates.length && (
                                            <Form.Text className="text-muted">
                                                No active templates.{' '}
                                                <Button
                                                    variant="link"
                                                    className="p-0 align-baseline"
                                                    onClick={() => navigate('/Graphe/dashboard/performance_templates')}
                                                >
                                                    Create or activate one
                                                </Button>
                                            </Form.Text>
                                        )}
                                    </Col>
                                    <Col md={6}>
                                        <Form.Label>
                                            Employees / levels
                                            {selectedEmployeeIds.length > 0 && (
                                                <span className="text-muted fw-normal ms-1">
                                                    ({selectedEmployeeIds.length} employee(s) selected)
                                                </span>
                                            )}
                                        </Form.Label>
                                        <MultiSelect
                                            value={selectionValues}
                                            options={groupedSelectOptions}
                                            optionGroupLabel="label"
                                            optionGroupChildren="items"
                                            onChange={(e) => setSelectionValues(e.value || [])}
                                            placeholder="Select employees or a level…"
                                            filter
                                            display="chip"
                                            className="w-100 pr-assign-multiselect"
                                            showClear
                                        />
                                        {!levelOptions.length && (
                                            <Form.Text className="text-muted">
                                                No org levels yet — set levels in Organization Hierarchy to enable bulk-by-level picks.
                                            </Form.Text>
                                        )}
                                    </Col>
                                    {selectedEmployees.length > 0 && (
                                        <Col md={12}>
                                            <div className="pr-assign-chip-list">
                                                {selectedEmployees.map((emp) => (
                                                    <span key={emp.user_id} className="pr-assign-chip">
                                                        <span>
                                                            {emp.name}
                                                            {emp.level != null && (
                                                                <em className="pr-assign-chip-level">L{emp.level}</em>
                                                            )}
                                                        </span>
                                                        <button
                                                            type="button"
                                                            className="pr-assign-chip-remove"
                                                            aria-label={`Remove ${emp.name}`}
                                                            onClick={() => removeSelectedEmployee(emp.user_id)}
                                                        >
                                                            ×
                                                        </button>
                                                    </span>
                                                ))}
                                            </div>
                                        </Col>
                                    )}
                                    <Col md={6}>
                                        <Form.Label>Period start</Form.Label>
                                        <Form.Control
                                            type="date"
                                            value={periodStart}
                                            onChange={(e) => setPeriodStart(e.target.value)}
                                        />
                                    </Col>
                                    <Col md={6}>
                                        <Form.Label>Period end</Form.Label>
                                        <Form.Control
                                            type="date"
                                            value={periodEnd}
                                            onChange={(e) => setPeriodEnd(e.target.value)}
                                        />
                                    </Col>
                                </Row>
                                <div className="d-flex gap-2 mt-3">
                                    <Button variant="primary" onClick={handleAssign} disabled={assigning}>
                                        {assigning
                                            ? 'Assigning…'
                                            : `Assign & notify${selectedEmployeeIds.length ? ` (${selectedEmployeeIds.length})` : ''}`}
                                    </Button>
                                    <Button
                                        variant="outline-secondary"
                                        onClick={() => {
                                            setShowAssign(false);
                                            resetAssignForm();
                                        }}
                                        disabled={assigning}
                                    >
                                        Cancel
                                    </Button>
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                )}

                <Col xs={6} md={3}>
                    <Card className="pr-stat-card h-100">
                        <Card.Body className="py-3">
                            <div className="pr-stat-value">{stats.total}</div>
                            <div className="pr-stat-label">Total reviews</div>
                        </Card.Body>
                    </Card>
                </Col>
                <Col xs={6} md={3}>
                    <Card className="pr-stat-card h-100">
                        <Card.Body className="py-3">
                            <div className="pr-stat-value text-secondary">{stats.draft}</div>
                            <div className="pr-stat-label">In progress</div>
                        </Card.Body>
                    </Card>
                </Col>
                <Col xs={6} md={3}>
                    <Card className="pr-stat-card h-100">
                        <Card.Body className="py-3">
                            <div className="pr-stat-value text-success">{stats.completed}</div>
                            <div className="pr-stat-label">Awaiting ack.</div>
                        </Card.Body>
                    </Card>
                </Col>
                <Col xs={6} md={3}>
                    <Card className="pr-stat-card h-100">
                        <Card.Body className="py-3">
                            <div className="pr-stat-value text-primary">{stats.acknowledged}</div>
                            <div className="pr-stat-label">Acknowledged</div>
                        </Card.Body>
                    </Card>
                </Col>

                <Col xs={12}>
                    <Card className="pr-list-card">
                        <Card.Header className="pr-list-card-header">
                            <div className="pr-filter-bar">
                                <InputGroup className="pr-search-input">
                                    <InputGroup.Text>
                                        <i className="pi pi-search" />
                                    </InputGroup.Text>
                                    <Form.Control
                                        placeholder="Search by name or email…"
                                        value={search}
                                        onChange={(e) => setSearch(e.target.value)}
                                    />
                                </InputGroup>

                                <div className="pr-month-wrap">
                                    <Form.Control
                                        type="month"
                                        className="pr-month-select pr-month-native"
                                        value={monthFilter}
                                        onChange={(e) => setMonthFilter(e.target.value)}
                                        title="Filter by review period month"
                                    />
                                    <span className="pr-month-display">
                                        {monthFilter ? moment(monthFilter, 'YYYY-MM').format('MMMM YYYY') : 'Month, Year'}
                                    </span>
                                </div>

                                <Form.Select
                                    className="pr-status-select"
                                    value={statusFilter}
                                    onChange={(e) => setStatusFilter(e.target.value)}
                                >
                                    <option value="all">All statuses</option>
                                    <option value="draft">Draft</option>
                                    <option value="in_progress">In progress</option>
                                    <option value="completed">Completed</option>
                                    <option value="acknowledged">Acknowledged</option>
                                </Form.Select>

                                {(monthFilter || search || statusFilter !== 'all') && (
                                    <Button
                                        variant="outline-secondary"
                                        size="sm"
                                        className="pr-clear-filters"
                                        onClick={() => {
                                            setMonthFilter('');
                                            setSearch('');
                                            setStatusFilter('all');
                                        }}
                                    >
                                        Clear filters
                                    </Button>
                                )}
                            </div>
                            <span className="pr-result-count">
                                {filtered.length} of {reviews.length} shown
                                {monthFilter ? ` · ${moment(monthFilter, 'YYYY-MM').format('MMM YYYY')}` : ''}
                            </span>
                        </Card.Header>

                        <Card.Body className="p-0">
                            {loading && (
                                <div className="pr-empty-state">
                                    <Spinner animation="border" size="sm" className="me-2" />
                                    Loading reviews…
                                </div>
                            )}

                            {!loading && !error && filtered.length === 0 && (
                                <div className="pr-empty-state">
                                    <h6 className="mb-2">No performance reviews found</h6>
                                    <p className="text-muted small mb-0">
                                        {reviews.length
                                            ? 'Try a different month, employee search, or status filter.'
                                            : 'Assign a template to employees to start self-reviews.'}
                                    </p>
                                </div>
                            )}

                            {!loading && filtered.length > 0 && (
                                <div className="table-responsive">
                                    <Table hover className="pr-table mb-0 align-middle">
                                        <thead>
                                            <tr>
                                                <th>Employee</th>
                                                <th>Template</th>
                                                <th>Period</th>
                                                <th>Score</th>
                                                <th>Classification</th>
                                                <th>Status</th>
                                                <th>Manager remark</th>
                                                <th />
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filtered.map((review) => (
                                                <tr key={review.review_id}>
                                                    <td>
                                                        <strong>{review.employee?.name || '—'}</strong>
                                                        {review.role_title && (
                                                            <div className="text-muted small">{review.role_title}</div>
                                                        )}
                                                    </td>
                                                    <td className="small">{review.template_name || '—'}</td>
                                                    <td className="small text-nowrap">
                                                        {moment(review.review_period_start).format('DD MMM YYYY')}
                                                        <span className="text-muted mx-1">–</span>
                                                        {moment(review.review_period_end).format('DD MMM YYYY')}
                                                    </td>
                                                    <td>
                                                        <span className="pr-score-cell">
                                                            {review.final_score != null ? review.final_score : '—'}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        {review.classification ? (
                                                            <span className={`pr-classification-badge ${classificationClass(review.classification)}`}>
                                                                {review.classification}
                                                            </span>
                                                        ) : (
                                                            <span className="text-muted">—</span>
                                                        )}
                                                    </td>
                                                    <td>
                                                        <Badge bg={STATUS_VARIANT[review.status] || 'secondary'} className="pr-status-badge">
                                                            {STATUS_LABEL[review.status] || review.status}
                                                        </Badge>
                                                    </td>
                                                    <td>
                                                        {review.has_manager_remark || review.manager_remark ? (
                                                            <Badge bg="success" className="pr-status-badge">Added</Badge>
                                                        ) : ['completed', 'acknowledged'].includes(review.status) ? (
                                                            <Badge bg="warning" text="dark" className="pr-status-badge">Pending</Badge>
                                                        ) : (
                                                            <span className="text-muted">—</span>
                                                        )}
                                                    </td>
                                                    <td className="text-end">
                                                        <Button
                                                            size="sm"
                                                            variant={review.status === 'completed' ? 'primary' : 'outline-primary'}
                                                            onClick={() =>
                                                                navigate(`/Graphe/dashboard/performance_review/${review.review_id}`)
                                                            }
                                                        >
                                                            {review.status === 'completed' ? 'Acknowledge' : 'Open'}
                                                        </Button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </div>
    );
};

export default PerformanceReviews;
